import { extractHTMLContent } from 'library-explorer';
import { Observable } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { GlossaryItem } from 'src/app/model/glossary-item';
import { LibraryService } from 'src/app/services/api/library.service';
import { Injectable } from '@angular/core';

@Injectable()
export class GlossaryHtmlService {
  private tooltipTextClass = 'tooltip-text';

  constructor(
    private readonly libraryService: LibraryService) {
  }

  public applyGlossaryTooltipsToHtml(value: string, element: HTMLElement): Observable<string> {
    return new Observable((subscriber) => {
      this.libraryService.getGlossaries()
        .pipe(
          map((glossaries: GlossaryItem[]) => {
            glossaries.forEach(glossary => {
              value = this.transformTextBasedOnGlossaries(value, glossary);
            });

            subscriber.next(value);
          }),
          delay(500),
          tap(() => {
            const newValue = this.setTooltipsPositions(value, element);

            subscriber.next(newValue);
            subscriber.complete();
          })
        ).subscribe();
    });
  }

  private setTooltipsPositions(value: string, parent: HTMLElement): string {
    const element = document.createElement('p');
    element.innerHTML = extractHTMLContent(value);
    parent.appendChild(element);
    const overflowHiddenParent = this.getOverflowHiddenParent(parent);
    const overflowHiddenParentRect = overflowHiddenParent?.getBoundingClientRect();
    Array.from(element.getElementsByClassName('glossary-tooltip')).forEach(item => {
      const itemRect = item.getBoundingClientRect();
      const topSpace = itemRect.top - (overflowHiddenParentRect?.top | 0);
      const leftSpace = itemRect.left - (overflowHiddenParentRect?.left | 0);
      const horizontalClassName = leftSpace < 240 ? 'position-left' : 'position-right';
      const verticalClassName = topSpace < 280 ? 'position-bottom' : 'position-top';
      item.classList.add(verticalClassName, horizontalClassName);
    });

    parent.removeChild(element);

    return element.innerHTML;
  }

  private getOverflowHiddenParent(element) {
    let parent = element.parentElement;

    while (parent) {
      const style = getComputedStyle(parent);
      if (
        style.overflow === "hidden" ||
        style.overflowX === "hidden" ||
        style.overflowY === "hidden"
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  }

  private transformTextBasedOnGlossaries(text: string, glossary: GlossaryItem): string {
    const element = document.createElement('p');
    element.innerHTML = text;

    const regex = new RegExp('\\b' + glossary.title + '\\b', 'gi');
    const description = extractHTMLContent(glossary.description).replace(/<(?!\/?span\b)[^>]+>/g, '');


    this.replaceTextInElement(element, regex, description);

    return element.innerHTML;
  }

  private replaceTextInElement(element: Node, regex: RegExp, text: string): void {

    element.childNodes.forEach(child => {
      if (child.parentElement && child.parentElement.classList.contains(this.tooltipTextClass)) {
        return;
      }

      switch (child.nodeType) {

        case Node.ELEMENT_NODE:
          this.replaceTextInElement(child, regex, text);
          break;
        case Node.TEXT_NODE:
          if (regex.test(child.textContent)) {
            const container = document.createElement('span');
            container.innerHTML = child.textContent.replace(regex,
              `<span class="glossary-tooltip">$&<span class="tooltip-container"><span class="${this.tooltipTextClass}">${text}</span></span></span>`);
            const nodesArray = Array.from(container.childNodes);
            child.replaceWith(...nodesArray);
          }

          break;
        case Node.DOCUMENT_NODE:
          this.replaceTextInElement(child, regex, text);
      }
    });
  }

}
